/**
 * StoryCard — shared story preview card (landing page teaser + stories grid).
 * Lives in its own module so the statically-loaded LandingPage doesn't pull
 * the whole StoriesPage chunk (audio player, book view, dialog stack) into
 * the initial bundle (route-splitting 2026-07-03). Extracted verbatim from
 * StoriesPage.tsx.
 */

import { useState, useRef, useCallback } from 'react'
import { BRAND_NAME } from './BrandLogo'
import { useT } from '@/i18n'
import type { Story } from '../data/stories'
import { DialogButton } from './dialog'

export const STORY_STORAGE_BASE = 'https://api-storage.arkturian.com/storage/media'

export function StoryCard({ story, compact, showButton = true, actionHref, actionLabel, onAction }: {
  story: Story
  compact?: boolean
  showButton?: boolean
  actionHref?: string
  actionLabel?: string
  onAction?: () => void
}) {
  const t = useT()
  const coverScene = story.coverImageId
    ? { illustrationId: story.coverImageId } as typeof story.scenes[0]
    : story.scenes.find(s => s.illustrationId || s.videoId)
  const cls = `sg-vdialog sg-vdialog--dark${compact ? ' sg-vdialog--compact' : ''}`
  const videoRef = useRef<HTMLVideoElement>(null)
  const fadeRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const handleMouseEnter = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    v.currentTime = 0
    v.volume = 0
    v.muted = false
    v.play().catch(() => {})
    // Fade in audio
    let vol = 0
    if (fadeRef.current) clearInterval(fadeRef.current)
    fadeRef.current = setInterval(() => {
      vol = Math.min(vol + 0.05, 0.4)
      v.volume = vol
      if (vol >= 0.4 && fadeRef.current) clearInterval(fadeRef.current)
    }, 50)
  }, [])

  const handleMouseLeave = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (fadeRef.current) clearInterval(fadeRef.current)
    // Fade out audio then pause
    let vol = v.volume
    fadeRef.current = setInterval(() => {
      vol = Math.max(vol - 0.05, 0)
      v.volume = vol
      if (vol <= 0) {
        if (fadeRef.current) clearInterval(fadeRef.current)
        v.pause()
        v.muted = true
      }
    }, 50)
  }, [])

  return (
    <div className={cls} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={onAction} style={{ cursor: onAction ? 'pointer' : undefined }}>
      <div className="sg-vdialog__content sg-vdialog__content--centered">
        <span className="comic-cover__label" style={{ color: story.color }}>
          {story.narrator === 'tschauko' ? t.stories.narratorTschauko : t.stories.narratorTschauka}
        </span>
        <h3 className="sg-dialog__title">{story.title}</h3>
        <p className="sg-vdialog__desc">{story.subtitle}</p>
        <div className="comic-cover__meta">
          <span>{t.stories.chapterCount(story.scenes.length)}</span>
          <span className="story-card__dot">&middot;</span>
          <span>{story.duration}</span>
          <span className="story-card__dot">&middot;</span>
          <span>{story.region}</span>
        </div>
        {/* Preview text hidden for cleaner layout */}
        {showButton && (actionHref ? (
          <a href={actionHref} className="sg-dialog__btn sg-dialog__btn--primary" style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}>
            {actionLabel || t.stories.startAudiobook}
          </a>
        ) : (
          <DialogButton variant="primary" style={{ width: '100%' }} onClick={onAction}>
            {actionLabel || t.stories.startAudiobook}
          </DialogButton>
        ))}
      </div>
      <div className="sg-vdialog__photo">
        {(story.coverMascots || story.title?.toLowerCase().includes('tschauko')) ? (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 0,
            background: 'linear-gradient(135deg, #1A1F16 0%, #2A3A23 100%)',
          }}>
            {(['tschauko', 'tschauka'] as const).map((char) => (
              <video
                key={char}
                muted
                loop
                autoPlay
                playsInline
                style={{
                  height: '80%',
                  objectFit: 'contain',
                  transform: char === 'tschauka' ? 'scaleX(-1)' : 'none',
                }}
              >
                <source src={`/frog-view/${char}-talk-alpha.mov`} type='video/quicktime; codecs="hvc1"' />
                <source src={`/frog-view/${char}-talk.webm`} type="video/webm" />
              </video>
            ))}
          </div>
        ) : coverScene?.videoId ? (
          <video
            ref={videoRef}
            className="sg-vdialog__video story-cover-video"
            src={`${STORY_STORAGE_BASE}/${coverScene.videoId}`}
            poster={`${STORY_STORAGE_BASE}/${coverScene.videoId}?format=jpg&timestamp=0&width=960`}
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : coverScene?.illustrationId ? (
          <img
            className="sg-vdialog__video"
            src={`${STORY_STORAGE_BASE}/${coverScene.illustrationId}?variant=medium`}
            alt={story.title}
            style={{ objectFit: 'cover' }}
            loading="lazy"
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, opacity: 0.3 }}>
            {story.cover}
          </div>
        )}
        <div className="sg-vdialog__fade sg-vdialog__fade--dark" style={{ pointerEvents: 'none' }} />
        <div className="sg-vdialog__brand-overlay" style={{ pointerEvents: 'none' }}>
          <img src="/logo-tscheppa-weiss.svg" alt="Tscheppaschlucht" className="sg-vdialog__brand-logo" />
          <span className="sg-vdialog__brand-text">{BRAND_NAME}</span>
        </div>
      </div>
    </div>
  )
}
